import { inject, Injectable } from '@angular/core';
import { DataTranslationModel } from '@models/data-translation-model';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { SessionService } from './session.service';
import { UserService } from './user.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';

@Injectable({
	providedIn: 'root'
})
export class LanguageService {

	private http = inject(HttpClient);

	public $onLangChange: Observable<string>;
	private onLangChangeSubscription = new Subject<string>();

	constructor(
		private translateService: TranslateService,
		private sessionService: SessionService,
		private userService: UserService
	) {
		translateService.onLangChange.subscribe((x: LangChangeEvent) => {
			this.onLangChangeSubscription.next(x.lang);
			document.documentElement.lang = x.lang;
		});

		this.$onLangChange = this.onLangChangeSubscription.asObservable();
	}

	public addLanguages(langs: Array<string>) {
		this.translateService.addLangs(langs);
	}

	public setDefaultLang(lang: string) {
		this.translateService.setFallbackLang(lang);
	}

	public async useLanguage(lang: string) {
		await firstValueFrom(this.translateService.use(lang));
		await this.ensureDataTranslations(lang);
		this.sessionService.set("language", lang);
	}

	public getLanguages(): Array<string> {
		return this.translateService.getLangs().slice();
	}

	public get currentLanguage(): string {
		return this.translateService.currentLang() ?? '';
	}

	public get defaultLanguage(): string {
		return this.translateService.getFallbackLang() ?? '';
	}

	public instantWithDefaultLanguage(key: string, interpolateParams?: any): string {
		if (this.defaultLanguage == this.currentLanguage) {
			const translation = this.instant(key, interpolateParams);
			return translation;
		}
		else {
			const translations = this.translateService.getTranslations(this.defaultLanguage);
			const translatedText: string = this.translateService.getParsedResult(key, interpolateParams) as string;
			return translatedText;
		}
	}

	public instant(key: string | Array<string>, interpolateParams?: any): string {
		return this.translateService.instant(key, interpolateParams);
	}

	public instantTranslateData(t?: DataTranslationModel | null, interpolateParams?: any): string {
		if (t?.TranslationKey) {
			const translation = this.translateService.instant(t.TranslationKey, interpolateParams);
			if (translation && translation !== t.TranslationKey) {
				return translation;
			}
		}

		if (!t?.Fallback) {
			return "";
		}

		return this.translateService.instant(t.Fallback, interpolateParams);
	}

	public hasTranslation(translationKey: string) {
		const currentTranslations = this.translateService.getTranslations(this.translateService.currentLang() ?? '');
		if (!currentTranslations) {
			return false;
		}
		const t = currentTranslations[translationKey];
		return t !== undefined;
	}

	public get(key: string | Array<string>, interpolateParams?: any) {
		return this.translateService.get(key, interpolateParams);
	}

	public async setSessionOrBrowserLanguage() {
		const sessionLanguage = this.sessionService.get<string>("language");
		if (sessionLanguage) {
			await firstValueFrom(this.translateService.use(sessionLanguage));
			await this.ensureDataTranslations(sessionLanguage);
		} else {
			const browserLang = this.translateService.getBrowserLang();
			if (this.translateService.getLangs().some(k => k == browserLang)) {
				if (browserLang)
					await this.useLanguage(browserLang);
			} else {
				await this.ensureDataTranslations(this.defaultLanguage);
				await this.useLanguage(this.defaultLanguage);
			}
		}
	}

	public isChina() {
		return window.location.host.indexOf('.cn') > -1 || window.location.host.indexOf('cn.') > -1;
	}

	private async ensureDataTranslations(lang: string) {
		const user = await this.userService.GetUser();
		if (user) {
			const dataTranslations = await this.getDataTranslations(lang);
			this.translateService.setTranslation(lang, dataTranslations, true);
		}
	}

	public async getDataTranslations(lang: string) {
		const t = await firstValueFrom(this.http.get<Array<{ Key: string, Value: string }>>(`${environment.dataUrl}/Translations/${lang.toUpperCase()}`));
		const target: { [key: string]: string } = {};
		t?.forEach(key => target[key.Key] = key.Value);
		return target;
	}

}
